/**
 * PbrMark — inline Plant Breeder's Rights symbol, rendered immediately
 * after a protected variety name.
 *
 * Placement rules come from IP Australia's "Industry guidelines for
 * labelling" (see feedback_pbr_symbol_display.md in project memory):
 *
 *   - Symbol sits on the same baseline as the variety name.
 *   - Gap between the last character of the name and the symbol equals
 *     the width of a lowercase "c" (~0.4em is a fair approximation).
 *   - Cap-height of the symbol box equals the height of the droplet
 *     section of the official artwork (we size to ~1em so it matches
 *     the surrounding text's cap-height).
 *
 * IMPORTANT: this component renders the symbol ONLY. You MUST also render
 * `<PbrNotice />` somewhere on the same view — the verbatim warning text
 * is part of the legal notice and the symbol on its own is non-compliant.
 *
 * Visibility is gated by the cultivar's protection status — pass either
 * the full Cultivar (recommended) or the raw status string. Hides itself
 * automatically when the cultivar is not under provisional/full protection.
 *
 * Artwork: official IP Australia "PBR symbol" PNG, two variants (black /
 * PMS 562 colour). Selected via the `color` prop.
 */
import type { Cultivar } from '../../types/cultivar';
import { isPbrProtected, type PbrColor } from '../../constants/pbr';
import pbrSymbolBlack from '../../assets/pbr/pbr-symbol-black.png';
import pbrSymbolColour from '../../assets/pbr/pbr-symbol-colour.png';

type Props = {
  /** Cultivar object; `protection_status` is read from it. */
  cultivar?: Pick<Cultivar, 'protection_status'> | null;
  /** Or pass the raw status directly (e.g. when rendering outside a cultivar context). */
  status?: string | null;
  /** Colour variant — 'black' (default) or 'pms562' (the official PBR teal). */
  color?: PbrColor;
  /** Optional aria-label override. Defaults to a descriptive label. */
  ariaLabel?: string;
  /** Extra className for the wrapper span. */
  className?: string;
};

export default function PbrMark({
  cultivar,
  status,
  color = 'black',
  ariaLabel,
  className,
}: Props) {
  const effectiveStatus = status ?? cultivar?.protection_status ?? null;
  if (!isPbrProtected(effectiveStatus)) return null;

  const src = color === 'pms562' ? pbrSymbolColour : pbrSymbolBlack;
  const label = ariaLabel ?? 'Plant Breeder’s Rights protected variety';

  return (
    <span
      className={className}
      // 0.4em ≈ width of a lowercase "c"; aligns symbol to text baseline; sized to cap-height.
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        marginLeft: '0.4em',
        verticalAlign: 'baseline',
        lineHeight: 1,
      }}
      aria-label={label}
      title={label}
    >
      <img
        src={src}
        alt=""
        aria-hidden="true"
        // Height matches surrounding cap-height; width follows aspect ratio.
        style={{ height: '1em', width: 'auto', display: 'block' }}
      />
    </span>
  );
}
